<template>
  <div class="resource-list">
    <!-- Center Group -->
    <div v-for="center in centers" :key="center.id" class="center-group mb-3">
      <!-- Center Header -->
      <div class="center-header d-flex justify-content-between align-items-center p-1 px-2 mb-2 rounded"
        @click="toggleCenter(center.id)">
        <div class="font-weight-bold d-flex align-items-center">
          <feather-icon :icon="isCenterCollapsed(center.id) ? 'ChevronRightIcon' : 'ChevronDownIcon'" size="16"
            class="mr-2" />
          {{ center.name }}
        </div>
        <div class="d-flex align-items-center">
          <span class="mr-2 operations-badge">{{ center.totalOperations }} Operações</span>
        </div>
      </div>

      <!-- Resource Rows (if not collapsed) -->
      <b-collapse :visible="!isCenterCollapsed(center.id)">
        <!-- Table Header (Visible only on desktop) -->
        <div class="d-none d-md-flex text-muted small mb-1 px-3">
          <b-row class="w-100 m-0">
            <b-col md="3">Recursos</b-col>
            <b-col md="1" class="text-center">Op</b-col>
            <b-col md="2" class="text-center">Produzindo</b-col>
            <b-col md="2" class="text-center">Setup</b-col>
            <b-col md="4">Ocupação</b-col>
          </b-row>
        </div>

        <!-- Resource Item -->
        <div v-for="resource in center.resources" :key="resource.id"
          class="resource-row-card bg-white rounded mb-2 shadow-sm">

          <!-- Main Row Content -->
          <div class="p-3 cursor-pointer" @click="toggleResource(resource.id)">
            <b-row class="align-items-center">

              <!-- Resource Name + Chevron -->
              <b-col cols="12" md="3" class="d-flex align-items-center mb-2 mb-md-0">
                <feather-icon :icon="isResourceExpanded(resource.id) ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="18"
                  class="mr-2" />
                <span class="font-weight-bold text-truncate" :title="resource.name">{{ resource.name }}</span>
              </b-col>

              <!-- Stats Grid -->
              <b-col cols="6" md="1" class="mb-2 mb-md-0 text-md-center">
                <span class="d-md-none text-muted small mr-1">Op:</span>
                {{ resource.operationCount }}
              </b-col>

              <b-col cols="6" md="2" class="mb-2 mb-md-0 text-md-center">
                <span class="d-md-none text-muted small mr-1">Prod:</span>
                {{ resource.producingTime }} h
              </b-col>

              <b-col cols="6" md="2" class="mb-2 mb-md-0 text-md-center">
                <span class="d-md-none text-muted small mr-1">Setup:</span>
                {{ resource.setupTime }} h
              </b-col>

              <!-- Orders Bar -->
              <b-col cols="12" md="4" class="mt-2 mt-md-0">
                <span class="d-md-none text-muted small mb-1 d-block">Ocupação:</span>
                <div class="d-flex rounded orders-scroll-container" style="height: 36px; overflow-x: auto;">
                  <div v-for="(order, idx) in resource.orders" :key="idx"
                    class="d-flex align-items-center justify-content-center text-white small" :class="order.class"
                    :style="{ minWidth: '25%', width: order.width + '%' }">
                    {{ order.id }}
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Expanded Details -->
          <b-collapse :visible="isResourceExpanded(resource.id)">
            <div class="p-3 border-top bg-light-secondary bg-opacity-10">
              <div class="row">
                <!-- Left: Order List -->
                <div class="col-md-4 border-right">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Ordens</h6>
                  </div>
                  <div class="mb-2">
                    <b-form-input size="sm" placeholder="Pesquisar" v-model="searchQuery" />
                  </div>
                  <b-button variant="outline-primary" size="sm" block class="mb-2"
                    @click="selectedOrder = null">Visualizar
                    Todas</b-button>
                  <div class="order-list-scroll" style="max-height: 150px; overflow-y: auto;">
                    <div v-for="(ord, i) in getFilteredOrders(resource)" :key="i"
                      class="py-1 px-2 mb-1 bg-white rounded border small cursor-pointer order-item"
                      :class="{ 'selected': selectedOrder && selectedOrder.number === ord.number }"
                      @click="selectOrder(ord)">
                      <div class="d-flex justify-content-between align-items-center">
                        <span>{{ ord.number }}</span>
                        <div class="status-indicator" :style="{ backgroundColor: ord.color }" :title="ord.status"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Right: Status Chart -->
                <div class="col-md-8">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">{{ getOrderStatusTitle(resource) }}</h6>
                    <b-button v-if="selectedOrder" variant="link" size="sm" class="p-0 text-muted"
                      @click="selectedOrder = null">
                      <feather-icon icon="XIcon" size="14" /> Limpar seleção
                    </b-button>
                  </div>

                  <div v-if="getOrderStatusDistribution(resource).length > 0">
                    <!-- Reuse the logic from OperationalStatusCard but simpler -->
                    <div class="progress-wrapper mb-2 mt-4">
                      <b-progress height="15px">
                        <b-progress-bar v-for="(st, idx) in getOrderStatusDistribution(resource)" :key="idx"
                          :value="st.value" :style="{ backgroundColor: st.color }"></b-progress-bar>
                      </b-progress>
                    </div>
                    <div class="row mt-3">
                      <div v-for="(st, idx) in getOrderStatusDistribution(resource)" :key="idx"
                        class="col-4 mb-2 small">
                        <div class="d-flex align-items-center">
                          <div class="indicator mr-1"
                            :style="{ backgroundColor: st.color, width: '4px', height: '12px' }"></div>
                          <span class="text-muted">{{ st.label }}</span>
                        </div>
                        <div class="font-weight-bold ml-2">{{ st.value }}%</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center p-3 text-muted">
                    Sem detalhes disponíveis
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { BCollapse, BButton, BFormInput, BProgress, BProgressBar, BRow, BCol } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    BCollapse,
    BButton,
    BFormInput,
    BProgress,
    BProgressBar,
    VueApexCharts,
    BRow,
    BCol
  },
  props: {
    centers: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      collapsedCenters: [],
      expandedResources: [],
      searchQuery: '',
      selectedOrder: null
    }
  },
  methods: {
    toggleCenter(id) {
      const index = this.collapsedCenters.indexOf(id);
      if (index === -1) {
        this.collapsedCenters.push(id);
      } else {
        this.collapsedCenters.splice(index, 1);
      }
    },
    isCenterCollapsed(id) {
      return this.collapsedCenters.includes(id);
    },
    toggleResource(id) {
      const index = this.expandedResources.indexOf(id);
      if (index === -1) {
        this.expandedResources.push(id);
      } else {
        this.expandedResources.splice(index, 1);
      }
    },
    isResourceExpanded(id) {
      return this.expandedResources.includes(id);
    },
    getFilteredOrders(resource) {
      if (!resource.details || !resource.details.orders) return [];
      if (!this.searchQuery) return resource.details.orders;

      const query = this.searchQuery.toLowerCase();
      return resource.details.orders.filter(ord =>
        ord.number.toLowerCase().includes(query)
      );
    },
    selectOrder(order) {
      this.selectedOrder = order;
    },
    getOrderStatusDistribution(resource) {
      if (this.selectedOrder) {
        // Return distribution for the selected order if available
        if (this.selectedOrder.statusDistribution) {
          return this.selectedOrder.statusDistribution;
        }
        // Fallback or if no distribution data
        return [];
      }
      // Default: Return resource overall distribution
      return resource.details && resource.details.status ? resource.details.status : [];
    },
    getOrderStatusTitle(resource) {
      if (this.selectedOrder) {
        return `Status da Ordem ${this.selectedOrder.number}`;
      }
      return 'Status do Recurso (Geral)';
    },
    getMiniChartOptions(value) {
      return {
        chart: { sparkline: { enabled: true } },
        colors: ['#F3811A'],
        plotOptions: {
          radialBar: {
            hollow: { size: '50%' },
            dataLabels: {
              show: true,
              name: { show: false },
              value: {
                offsetY: 5,
                fontSize: '14px',
                fontWeight: '600',
                color: '#5e5873',
                show: true,
                formatter: () => `${value}%`
              }
            }
          }
        },
        stroke: { lineCap: 'round' }
      }
    }
  }
}
</script>

<style scoped>
.resource-row-card {
  transition: all 0.3s ease;
}

.resource-row-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.1) !important;
}

.center-header {
  background-color: #FAF3EE;
  color: #4C4541;
  cursor: pointer;
}

.operations-badge {
  background-color: #fff;
  color: #998F8A;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.bg-light-secondary {
  background-color: #d0d2d6;
}

.bg-opacity-10 {
  background-color: rgba(0, 0, 0, 0.02);
}

.orders-scroll-container::-webkit-scrollbar {
  height: 8px;
}



.orders-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.orders-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.orders-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.order-item.selected {
  background-color: #FAF3EE !important;
  border-color: #F3811A !important;
  font-weight: 600;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
</style>
